import React, { useDeferredValue, useState, useRef } from 'react';
import {
  ArrowDown,
  ChevronDown,
  ExternalLink,
  Globe,
  Mail,
  MapPin,
  MapPinned,
  Phone,
  Search,
  ShieldCheck,
  Wrench,
  X
} from 'lucide-react';
import {
  SERVICE_NETWORK,
  type ProvinceNetwork,
  type RegionNetwork,
  type ServicePoint
} from '../data/serviceNetwork';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { useSEO } from '../hooks/useSEO';

const GOOGLE_MAP_EMBED_URL =
  'https://www.google.com/maps/d/u/1/embed?mid=1iGMUO3kxIIAm-Wp4R_OGepBa48Xgy9g&ehbc=2E312F';
const GOOGLE_MAP_VIEW_URL =
  'https://www.google.com/maps/d/u/1/viewer?mid=1iGMUO3kxIIAm-Wp4R_OGepBa48Xgy9g';
const REPAIR_FORM_URL = 'https://reparacion.zamelux.com/';

const howItWorks = [
  {
    label: 'Busca por zona',
    description: 'Filtra por ciudad, provincia, nombre comercial o un dato de contacto.'
  },
  {
    label: 'Ubica el punto',
    description: 'Abre el mapa oficial o entra directamente en la región que te interesa.'
  },
  {
    label: 'Contacta o solicita recogida',
    description: 'Habla con el SAT autorizado o pide una recogida si no tienes uno cerca.'
  }
];

function getPointCount(provinces: ProvinceNetwork[]) {
  return provinces.reduce((sum, province) => sum + province.servicePoints.length, 0);
}

function normalizeSearchValue(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

function slugify(value: string) {
  return normalizeSearchValue(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizeEmail(value?: string) {
  const normalized = value?.replace(/\u00a0/g, '').trim();
  return normalized ? normalized : null;
}

function normalizeWebsiteUrl(value?: string) {
  if (!value) {
    return null;
  }

  let normalized = value.replace(/\u00a0/g, '').trim();

  if (!normalized || normalized === 'https://' || normalized === 'http://') {
    return null;
  }

  normalized = normalized.replace(/^https?:\/\/https?:\/\//i, 'https://');

  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }

  return normalized;
}

function getVisiblePhones(point: ServicePoint) {
  return (point.phones ?? []).map((phone) => phone.trim()).filter(Boolean);
}

function pointMatchesQuery(point: ServicePoint, query: string) {
  const searchableFields = [
    point.name,
    ...point.address,
    ...getVisiblePhones(point),
    normalizeEmail(point.email) ?? '',
    normalizeWebsiteUrl(point.website) ?? ''
  ];

  return normalizeSearchValue(searchableFields.join(' ')).includes(query);
}

function filterNetwork(query: string): RegionNetwork[] {
  if (!query) {
    return SERVICE_NETWORK;
  }

  return SERVICE_NETWORK.map((region) => {
    const regionMatches = normalizeSearchValue(region.region).includes(query);

    const provinces = region.provinces
      .map((province) => {
        const provinceMatches = normalizeSearchValue(province.province).includes(query);
        const servicePoints = province.servicePoints.filter((point) => {
          if (regionMatches || provinceMatches) {
            return true;
          }

          return pointMatchesQuery(point, query);
        });

        return {
          ...province,
          servicePoints
        };
      })
      .filter((province) => province.servicePoints.length > 0);

    return {
      ...region,
      provinces
    };
  }).filter((region) => region.provinces.length > 0);
}

function MetricCard({
  value,
  label,
  description
}: {
  value: string;
  label: string;
  description: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/[0.02] p-6 shadow-[0_8px_32px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all duration-500 hover:-translate-y-1 hover:border-white/20 hover:from-white/[0.15] hover:to-white/5 hover:shadow-[0_20px_48px_rgba(0,0,0,0.25)]">
      <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/5 blur-2xl transition-all duration-500 group-hover:bg-white/10 group-hover:blur-3xl" />
      <div className="relative z-10">
        <p className="origin-left text-3xl font-bold tracking-tight text-white drop-shadow-sm transition-transform duration-500 group-hover:scale-[1.02] group-hover:transform-gpu md:text-[2rem]">{value}</p>
        <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">{label}</p>
        <p className="mt-3 text-sm font-medium leading-6 text-white/70 transition-colors group-hover:text-white/90">{description}</p>
      </div>
    </div>
  );
}

function ServicePointCard({ point }: { point: ServicePoint }) {
  const visiblePhones = getVisiblePhones(point);
  const email = normalizeEmail(point.email);
  const website = normalizeWebsiteUrl(point.website);
  const websiteLabel = website?.replace(/^https?:\/\//i, '').replace(/\/+$/, '');

  return (
    <article className="group relative overflow-hidden rounded-[1.6rem] border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-500 hover:-translate-y-1 hover:border-black/10 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,rgba(0,0,0,0.015)_100%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="relative z-10 flex items-start justify-between gap-4">
        <div>
          <span className="inline-flex rounded-full bg-[color:var(--zw-ivory)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[color:var(--zw-moss)] ring-1 ring-black/5 transition-colors group-hover:bg-white">
            SAT autorizado
          </span>
          <h4 className="mt-3 text-xl font-bold tracking-tight text-gray-900 transition-colors group-hover:text-black">{point.name}</h4>
        </div>
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[color:var(--zw-ivory)] text-[color:var(--zw-moss)] shadow-sm ring-1 ring-black/5 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white">
          <ShieldCheck className="h-6 w-6 stroke-[2]" />
        </div>
      </div>

      <div className="relative z-10 mt-5 space-y-4 text-sm leading-6 text-gray-600">
        <div className="group/item flex items-start gap-3">
          <div className="mt-0.5 rounded-full bg-gray-50 p-1.5 transition-colors group-hover/item:bg-[color:var(--zw-ivory)]">
            <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover/item:text-[color:var(--zw-moss)]" />
          </div>
          {/* 将多行地址组合为一整段紧凑的内联文本，避免卡片纵向空间被层层拉伸 */}
          <div className="pt-1 text-[13px] font-medium leading-relaxed text-gray-600">
            {point.address.join(' · ')}
          </div>
        </div>

        {(visiblePhones.length > 0 || email || website) && (
          <div className="space-y-3 border-t border-black/5 pt-4">
            {visiblePhones.length > 0 && (
              <div className="group/item flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-gray-50 p-1.5 transition-colors group-hover/item:bg-[color:var(--zw-ivory)]">
                  <Phone className="h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover/item:text-[color:var(--zw-moss)]" />
                </div>
                <div className="space-y-1 pt-1 font-medium">
                  {visiblePhones.map((phone) => (
                    <a
                      key={phone}
                      href={`tel:${phone.replace(/\s+/g, '')}`}
                      className="block transition-colors hover:text-[color:var(--zw-moss)]"
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {email && (
              <div className="group/item flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-gray-50 p-1.5 transition-colors group-hover/item:bg-[color:var(--zw-ivory)]">
                  <Mail className="h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover/item:text-[color:var(--zw-moss)]" />
                </div>
                <a href={`mailto:${email}`} className="break-all pt-1 font-medium transition-colors hover:text-[color:var(--zw-moss)]">
                  {email}
                </a>
              </div>
            )}

            {website && websiteLabel && (
              <div className="group/item flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-gray-50 p-1.5 transition-colors group-hover/item:bg-[color:var(--zw-ivory)]">
                  <Globe className="h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover/item:text-[color:var(--zw-moss)]" />
                </div>
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all pt-1 font-medium transition-colors hover:text-[color:var(--zw-moss)]"
                >
                  {websiteLabel}
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

// ==============================================================
// 视图控制层：ServiceNetwork 页面主容器
// 描述：负责大屏检索交互、DOM结构渲染、搜索折叠动画，以及卡片列表和地图的展示组装。
// ==============================================================
export default function ServiceNetwork() {
  // 维护当前处于展开可见状态的“大区（Region）”
  const [openRegion, setOpenRegion] = useState<string>(SERVICE_NETWORK[0]?.region ?? '');
  
  // 绑定全局搜索输入框的状态
  const [searchQuery, setSearchQuery] = useState('');
  
  // 用于点击“查看结果”或者回车键时，页面丝滑滚动下钻到结果目录的底层引用
  const resultsRef = useRef<HTMLDivElement>(null);

  // 监听键盘按键：检测回车以滚动至结果展示区
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // 性能优化：通过延迟值降低连续键入时的大规模 React 重绘，从而保障搜索框不卡顿
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const normalizedQuery = normalizeSearchValue(deferredSearchQuery.trim());
  const filteredNetwork = filterNetwork(normalizedQuery);
  
  // 核心查询标识：判断进入“极简检索模式”触发折叠特效
  const isSearching = normalizedQuery.length > 0;
  
  // ===== 用于页面总揽计数及搜索结果实时反馈显示的数据项 =====
  const visibleNetwork = filteredNetwork;
  const totalRegions = SERVICE_NETWORK.length;
  const totalProvinces = SERVICE_NETWORK.reduce((sum, region) => sum + region.provinces.length, 0);
  const totalPoints = SERVICE_NETWORK.reduce((sum, region) => sum + getPointCount(region.provinces), 0);
  const visibleRegions = visibleNetwork.length;
  const visiblePoints = visibleNetwork.reduce((sum, region) => sum + getPointCount(region.provinces), 0);

  useSEO({
    title: 'Red Oficial de Servicio Técnico',
    description:
      'Consulta el mapa oficial de puntos de asistencia técnica Zwheel y encuentra los datos de contacto de cada centro autorizado.',
    keywords:
      'servicio técnico Zwheel, red oficial Zwheel, SAT Zwheel, reparación patinetes, asistencia técnica'
  });

  return (
    <div className="min-h-screen bg-[#f7f2ea]">
      <section className="relative overflow-hidden bg-[#0f1412] text-white">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20 mix-blend-luminosity"
            style={{ backgroundImage: 'url("/Homepage_2-05-scaled1.jpg")' }}
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.12),transparent_35%),radial-gradient(circle_at_75%_15%,rgba(221,201,171,0.25),transparent_30%),linear-gradient(180deg,rgba(12,16,15,0.85)_0%,rgba(15,20,18,0.98)_100%)]" />
          <div className="absolute -left-32 top-10 h-96 w-96 animate-pulse rounded-full bg-[#ddc9ab]/15 blur-[100px]" style={{ animationDuration: '6s' }} />
          <div className="absolute bottom-0 right-[5%] h-80 w-80 animate-pulse rounded-full bg-[#204a38]/30 blur-[100px]" style={{ animationDuration: '8s' }} />
        </div>

        <div className="relative mx-auto max-w-[1520px] px-4 pb-16 pt-28 md:px-8 md:pb-20 md:pt-40 xl:px-12">
          <div className="max-w-5xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-gradient-to-r from-white/10 to-white/5 py-1.5 pl-2 pr-4 text-sm font-medium tracking-wide text-white/90 shadow-[0_4px_24px_rgba(0,0,0,0.2)] backdrop-blur-md">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                <Wrench className="h-3.5 w-3.5 text-white" />
              </span>
              Soporte técnico oficial
            </div>
            <h1 className="mt-7 max-w-5xl text-[3rem] font-bold tracking-tight md:text-6xl xl:text-[5.5rem] xl:leading-[0.92]">
              <span className="bg-gradient-to-br from-white via-white/90 to-white/50 bg-clip-text text-transparent">Red oficial de</span><br className="hidden xl:block" />
              <span className="bg-gradient-to-br from-white via-white/80 to-[#ddc9ab] bg-clip-text text-transparent xl:ml-3">servicio técnico</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg font-light leading-8 text-white/60 md:text-xl">
              Encuentra tu SAT autorizado más cercano por región, provincia, ciudad o nombre comercial.
              Mapas claros, contactos directos y un directorio diseñado para que de verdad lo puedas usar.
            </p>
          </div>

          <div className="mt-10 rounded-[2.4rem] border border-white/10 bg-white/[0.04] p-4 shadow-[0_28px_80px_rgba(0,0,0,0.22)] backdrop-blur-md transition-colors hover:bg-white/[0.06] md:p-5">
            <div className="flex flex-col gap-3">
              <p className="pl-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">
                Busca tu punto de servicio
              </p>
              
              <div className="flex flex-col xl:flex-row xl:items-center gap-4">
                <div className="min-w-0 flex-1">
                  <label className="relative block">
                    <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="Ciudad, provincia, tienda, teléfono o email"
                      className="h-[60px] w-full rounded-2xl border-2 border-white/20 bg-white/95 px-14 pr-14 text-[15px] font-medium text-gray-900 outline-none transition-all placeholder:text-gray-500 hover:bg-white focus:border-transparent focus:bg-white focus:ring-4 focus:ring-black/10"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-black"
                        aria-label="Limpiar búsqueda"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </label>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row xl:flex-shrink-0">
                  <Button asChild className="h-[60px] rounded-2xl bg-white px-7 text-[15px] font-semibold text-black transition-all hover:scale-[1.02] hover:bg-gray-100 hover:shadow-md active:scale-95">
                    <a href={GOOGLE_MAP_VIEW_URL} target="_blank" rel="noopener noreferrer">
                      Abrir en mapa
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>

                  <Button
                    asChild
                    variant="outline"
                    className="h-[60px] rounded-2xl border-2 border-white/20 bg-transparent px-7 text-[15px] font-semibold text-white transition-all hover:bg-white/10 hover:text-white active:scale-95"
                  >
                    <a href={REPAIR_FORM_URL} target="_blank" rel="noopener noreferrer">
                      Solicitar reparación
                      <Wrench className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-3 pl-1 text-sm font-medium text-white/70">
                {isSearching ? (
                  <>
                    <span>
                      {visiblePoints} puntos visibles en {visibleRegions} regiones para "{deferredSearchQuery.trim()}".
                    </span>
                    <button
                      onClick={() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/10 px-3.5 py-1.5 font-medium text-white shadow-sm transition-all hover:bg-white/20 hover:shadow"
                    >
                      Ver resultados
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : (
                  <span>Filtra por ubicación o contacto y baja directamente a la parte que te interesa.</span>
                )}
              </div>
            </div>
          </div>

          {/* 顶部的数据概览板块：它受限于处于“搜索状态”进行平滑隐藏特效，把屏幕空间还给主要信息 */}
          <div
            className={cn(
              'grid transition-all duration-500 ease-in-out',
              isSearching ? 'grid-rows-[0fr] opacity-0 mt-0' : 'grid-rows-[1fr] opacity-100 mt-6'
            )}
          >
            {/* 这里的 min-h-0 为 CSS Grid 高度完全折叠到 0 所需，它消除了遗留区域导致布局错乱的问题 */}
            <div className="overflow-hidden min-h-0">
              <div className="grid gap-4 pt-1 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard
                  value={String(totalRegions)}
              label="Regiones"
              description="Agrupadas para que entres rápido en la zona que necesitas y no recorras una lista infinita."
            />
            <MetricCard
              value={String(totalProvinces)}
              label="Provincias"
              description="Cada provincia muestra sus puntos activos con dirección, teléfono, correo y web."
            />
            <MetricCard
              value={String(totalPoints)}
              label="Puntos activos"
              description="Centros autorizados publicados y disponibles para localizar en mapa o directorio."
            />
            <div className="group relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/[0.02] p-6 shadow-[0_8px_32px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all duration-500 hover:border-white/20 hover:from-white/[0.15] hover:to-white/5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">Cómo funciona</p>
              <div className="relative z-10 mt-5 space-y-4">
                {howItWorks.map((item, index) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <span className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white shadow-inner ring-1 ring-white/20 transition-transform duration-500 group-hover:rotate-[360deg]">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-semibold text-white/90 drop-shadow-sm">{item.label}</p>
                      <p className="mt-1 text-sm font-medium leading-6 text-white/60">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative -mt-8 bg-[linear-gradient(180deg,#f5efe6_0%,#ffffff_24%,#fbf7f0_100%)] pb-20 md:-mt-12 md:pb-24">
        <div className="mx-auto max-w-[1520px] px-4 md:px-8 xl:px-12">
          {/* 将外部包裹层配置为基于间距平滑变换，从而一并消解包含在内的间隙（防止遗留排版错乱块） */}
          <div className={cn("transition-all duration-700 ease-in-out", isSearching ? "space-y-0" : "space-y-6")}>
            
            {/* 总体地图（Google Map）概览：如果用户正使用输入框精确筛选，该版块直接渐隐折叠脱离视口 */}
            <div
              className={cn(
                'grid transition-all duration-700 ease-in-out',
                isSearching ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
              )}
            >
              <div className="overflow-hidden min-h-0">
                <AnimatedSection>
                  <div className="overflow-hidden rounded-[2.6rem] border border-black/5 bg-white shadow-[0_28px_90px_rgba(0,0,0,0.09)]">
                <div className="flex flex-col gap-6 border-b border-black/5 px-6 py-6 md:px-8 lg:flex-row lg:items-end lg:justify-between">
                  <div className="max-w-3xl">
                    <p className="text-sm font-bold uppercase tracking-[0.16em] text-[color:var(--zw-moss)]">
                      Mapa multipunto
                    </p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
                      Vista general de la red oficial Zwheel
                    </h2>
                    <p className="mt-3 text-base leading-7 text-gray-600">
                      Si prefieres ubicarte visualmente primero, aquí tienes el mapa oficial con todos los puntos de
                      servicio técnico autorizados.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <div className="inline-flex items-center gap-2 rounded-full bg-[color:var(--zw-ivory)] px-4 py-2 text-sm font-medium text-gray-700">
                      <MapPinned className="h-4 w-4 text-[color:var(--zw-moss)]" />
                      {isSearching ? `${visiblePoints} resultados visibles` : `${totalPoints} puntos publicados`}
                    </div>
                    <div className="inline-flex items-center rounded-full border border-black/10 px-4 py-2 text-sm font-medium text-gray-600">
                      Directorio por regiones
                    </div>
                  </div>
                </div>

                <iframe
                  src={GOOGLE_MAP_EMBED_URL}
                  title="Mapa oficial de servicio técnico Zwheel"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="h-[480px] w-full md:h-[640px]"
                />
                  </div>
                </AnimatedSection>
              </div>
            </div>

            <AnimatedSection delay={0.08}>
              <div
                ref={resultsRef}
                className="scroll-mt-24 rounded-[2.4rem] border border-black/5 bg-white p-6 shadow-[0_18px_50px_rgba(0,0,0,0.05)] md:p-8"
              >
                <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                  <div className="max-w-3xl">
                    <p className="text-sm font-bold uppercase tracking-[0.16em] text-[color:var(--zw-moss)]">
                      {isSearching ? 'Resultados filtrados' : 'Directorio técnico'}
                    </p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
                      {isSearching
                        ? `Mostrando ${visiblePoints} coincidencias en ${visibleRegions} regiones`
                        : 'Accede por región y baja solo al bloque que necesitas'}
                    </h2>
                    <p className="mt-3 text-base leading-7 text-gray-600">
                      {isSearching
                        ? `La búsqueda "${deferredSearchQuery.trim()}" deja visibles solo los SAT relevantes para esa consulta.`
                        : 'Hemos compactado la información en bloques anchos, claros y escaneables para evitar la sensación de directorio interminable.'}
                    </p>
                  </div>

                  <div className="xl:max-w-[760px]">
                    <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">
                      Saltar a una región
                    </p>
                    <div className="flex flex-wrap gap-2.5">
                      {visibleNetwork.map((region) => (
                        <a
                          key={region.region}
                          href={`#region-${slugify(region.region)}`}
                          onClick={() => setOpenRegion(region.region)}
                          className="inline-flex items-center rounded-full border border-black/5 bg-gray-50 px-4 py-2 text-[13px] font-medium text-gray-600 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-[color:var(--zw-moss)] hover:text-white hover:shadow-md"
                        >
                          {region.region}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.16}>
              {visibleNetwork.length === 0 ? (
                <div className="rounded-[2.3rem] border border-dashed border-black/15 bg-white p-12 text-center shadow-sm">
                  <p className="text-sm font-bold uppercase tracking-[0.16em] text-gray-500">Sin coincidencias</p>
                  <h3 className="mt-3 text-3xl font-bold tracking-tight text-gray-900">
                    No hemos encontrado ningún SAT con esa búsqueda.
                  </h3>
                  <p className="mx-auto mt-3 max-w-2xl text-base leading-7 text-gray-600">
                    Prueba con otra ciudad, provincia o nombre comercial. También puedes abrir el mapa completo para
                    revisar la red visualmente.
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  {visibleNetwork.map((region) => {
                    const isOpen = isSearching || openRegion === region.region;
                    const regionPointCount = getPointCount(region.provinces);

                    return (
                      <section
                        id={`region-${slugify(region.region)}`}
                        key={region.region}
                        className={cn(
                          'group overflow-hidden rounded-[2.5rem] border bg-white shadow-[0_12px_40px_rgba(0,0,0,0.03)] transition-all duration-500',
                          isOpen ? 'border-black/10 shadow-[0_20px_50px_rgba(0,0,0,0.06)]' : 'border-black/5 hover:-translate-y-1 hover:shadow-[0_16px_45px_rgba(0,0,0,0.05)]'
                        )}
                      >
                        <button
                          type="button"
                          disabled={isSearching}
                          onClick={() => setOpenRegion(isOpen ? '' : region.region)}
                          className={cn(
                            'flex w-full flex-col gap-4 px-6 py-6 text-left outline-none transition-colors md:px-8 md:py-7 lg:flex-row lg:items-end lg:justify-between',
                            isOpen
                              ? 'bg-[linear-gradient(90deg,#fcfaf6_0%,#ffffff_100%)]'
                              : 'bg-white hover:bg-gray-50/80',
                            isSearching ? 'cursor-default' : ''
                          )}
                        >
                          <div>
                            <span className="inline-flex rounded-full bg-black px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-sm transition-colors group-hover:bg-[#18392b]">
                              Región
                            </span>
                            <div className="mt-4 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
                              <h3 className="text-3xl font-bold tracking-tight text-gray-900 transition-colors md:text-4xl">
                                {region.region}
                              </h3>
                              <span className="inline-flex self-start rounded-full border border-black/5 bg-gray-50 px-4 py-1.5 text-sm font-medium text-gray-600 shadow-sm transition-colors group-hover:bg-white">
                                {region.provinces.length} provincias
                              </span>
                              <span className="inline-flex self-start rounded-full border border-black/5 bg-gray-50 px-4 py-1.5 text-sm font-medium text-gray-600 shadow-sm transition-colors group-hover:bg-white">
                                {regionPointCount} puntos activos
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="hidden max-w-[420px] flex-wrap justify-end gap-2 lg:flex">
                              {region.provinces.slice(0, 4).map((province) => (
                                <span
                                  key={province.province}
                                  className="inline-flex rounded-full bg-black/[0.03] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 transition-colors group-hover:bg-white group-hover:shadow-sm"
                                >
                                  {province.province}
                                </span>
                              ))}
                              {region.provinces.length > 4 && (
                                <span className="inline-flex rounded-full bg-black/[0.03] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 transition-colors group-hover:bg-white group-hover:shadow-sm">
                                  +{region.provinces.length - 4} más
                                </span>
                              )}
                            </div>

                            {!isSearching && (
                              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-50 transition-colors group-hover:bg-white group-hover:shadow-sm">
                                <ChevronDown
                                  className={cn(
                                    'h-5 w-5 stroke-[2] text-gray-500 transition-transform duration-500',
                                    isOpen ? 'rotate-180 text-black' : 'group-hover:text-black'
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        </button>

                        {isOpen && (
                          <div className="border-t border-black/5 bg-[#fcfaf6] px-6 py-6 md:px-8 md:py-8">
                            <div className="space-y-6">
                              {region.provinces.map((province, pIndex) => (
                                <div key={province.province} className={cn("pb-4", pIndex > 0 && "pt-8 border-t border-black/[0.03]")}>
                                  {/* 取消省份原本粗糙的物理宽框（嵌套套娃设计），降层它成为一个纯粹清朗的透明标题栏。 */}
                                  <div className="mb-6 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <h4 className="text-xl font-bold tracking-tight text-gray-900 md:text-2xl">
                                        {province.province}
                                      </h4>
                                      <span className="inline-flex items-center rounded-full bg-black/5 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-gray-600 shadow-sm">
                                        {province.servicePoints.length} puntos
                                      </span>
                                    </div>
                                  </div>

                                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                                    {province.servicePoints.map((point) => (
                                      <ServicePointCard
                                        key={`${province.province}-${point.name}-${point.address[0]}`}
                                        point={point}
                                      />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              )}
            </AnimatedSection>

            <AnimatedSection delay={0.22}>
              <div className="group relative overflow-hidden rounded-[2.6rem] border border-black/5 bg-[linear-gradient(135deg,#f4ede2_0%,#fbf7f0_58%,#ffffff_100%)] shadow-[0_20px_60px_rgba(0,0,0,0.06)] transition-all duration-500 hover:shadow-[0_28px_80px_rgba(0,0,0,0.08)]">
                <div className="absolute -right-64 -top-64 h-[500px] w-[500px] rounded-full bg-white/40 blur-[100px] transition-transform duration-1000 group-hover:translate-x-20 group-hover:translate-y-20" />
                <div className="relative z-10 grid gap-8 px-8 py-8 md:px-10 md:py-10 xl:grid-cols-[1.1fr_0.9fr] xl:px-12 xl:py-12">
                  <div className="max-w-4xl">
                    <span className="inline-flex rounded-full border border-black/10 bg-white/80 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-600 shadow-sm backdrop-blur-md">
                      Recogida a domicilio
                    </span>
                    <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 md:text-5xl">
                      ¿No tienes un SAT cerca? También podemos recoger tu patinete.
                    </h2>
                    <p className="mt-5 text-lg font-medium leading-8 text-gray-600">
                      Si no te encaja ninguno de los puntos anteriores, puedes solicitar una recogida para reparación.
                      Cuando la avería no entra en garantía, el transporte puede tener más plazo y coste, pero sigues
                      teniendo una vía clara para recuperar el equipo reparado y operativo.
                    </p>
                    <div className="mt-10">
                      <Button asChild className="h-[60px] rounded-2xl bg-black px-8 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.12)] transition-all hover:-translate-y-1 hover:bg-gray-900 hover:shadow-[0_16px_40px_rgba(0,0,0,0.2)] active:scale-95">
                        <a href={REPAIR_FORM_URL} target="_blank" rel="noopener noreferrer">
                          Ir al formulario de recogida
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-[2.2rem] border border-white bg-white/40 p-7 shadow-[0_8px_30px_rgba(0,0,0,0.04)] backdrop-blur-xl md:p-8">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">Qué ocurre después</p>
                    <div className="mt-6 space-y-6">
                      {[
                        'Rellenas el formulario con tu modelo y la incidencia.',
                        'El equipo coordina la recogida y revisa la reparación.',
                        'Recibes tu patinete reparado listo para volver a circular.'
                      ].map((step, index) => (
                        <div key={step} className="group/step flex items-start gap-4">
                          <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-gray-900 shadow-sm ring-1 ring-black/5 transition-transform duration-500 group-hover/step:rotate-[360deg] group-hover/step:bg-[color:var(--zw-ivory)] group-hover/step:text-[color:var(--zw-moss)]">
                            {index + 1}
                          </span>
                          <p className="pt-1 text-[15px] font-medium leading-6 text-gray-700 transition-colors group-hover/step:text-gray-900">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
